/**
 * Sponsor console data hooks (NOS-164). All reads ride the additive
 * `<table>_select_sponsor` RLS policies from NOS-163 — no service-role calls.
 */
import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { errorMessage } from "@/lib/errorMessage"
import { useOrgStore } from "@/stores/orgStore"
import { usePermission } from "@/hooks/usePermission"
import {
  aggregatePortfolio,
  type PortfolioRollup,
  type SponsorDonationRow,
  type SponsorshipWithChild,
} from "@/lib/sponsor"

// v1 cap on cross-org donation fetches (AdminDashboard precedent). A sponsor
// portfolio past this size needs a server-side aggregate — revisit in Phase 2
// with the fund ledger.
const DONATIONS_FETCH_CAP = 5000

const SPONSORSHIP_SELECT =
  "id, child_org_id, mode, status, admin_fee_pct, tags, started_at, child:organizations!sponsorships_child_org_id_fkey(id, name, slug, logo_url)"

export interface SponsorPortfolio {
  loading: boolean
  /** Current org has at least one sponsorship row as sponsor. */
  isSponsor: boolean
  sponsorships: SponsorshipWithChild[]
  donations: SponsorDonationRow[]
  rollup: PortfolioRollup
  campaignTitlesById: Map<string, string>
  memberCountsByOrg: Map<string, number>
  contactCountsByOrg: Map<string, number>
  error: string | null
  refresh: () => void
}

const EMPTY_ROLLUP: PortfolioRollup = {
  totalRaisedCents: 0,
  raisedThisMonthCents: 0,
  byOrg: new Map(),
}

/**
 * Lightweight gate check: is the current org a sponsor org and does the user
 * hold can_view_child_financials there? Drives the nav entry.
 *
 * NOS-196: "sponsor org" means ANY sponsor signal — an existing sponsorship OR
 * billing state (subscription status ≠ none, or the manual-invoice flag). An
 * entitled sponsor mid-cold-start (subscribed, zero children yet) needs the
 * nav entry or there is no path back to the console after checkout. Members
 * without the permission are unchanged (isSponsor stays false).
 */
export function useIsSponsor(): { isSponsor: boolean; loading: boolean } {
  const { currentOrg } = useOrgStore()
  const { hasPermission, loading: permLoading } = usePermission("can_view_child_financials")
  const [hasSponsorSignal, setHasSponsorSignal] = useState(false)
  const [signalLoading, setSignalLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function check() {
      if (!currentOrg?.id) {
        setHasSponsorSignal(false)
        setSignalLoading(false)
        return
      }
      setSignalLoading(true)
      const [countRes, orgRes] = await Promise.all([
        supabase
          .from("sponsorships")
          .select("id", { count: "exact", head: true })
          .eq("sponsor_org_id", currentOrg.id),
        supabase
          .from("organizations")
          .select("sponsor_subscription_status, sponsor_manual_active")
          .eq("id", currentOrg.id)
          .maybeSingle(),
      ])
      if (cancelled) return
      const billing = orgRes.data as {
        sponsor_subscription_status: string
        sponsor_manual_active: boolean
      } | null
      setHasSponsorSignal(
        (!countRes.error && (countRes.count ?? 0) > 0) ||
          (billing != null &&
            (billing.sponsor_manual_active || billing.sponsor_subscription_status !== "none"))
      )
      setSignalLoading(false)
    }
    check()
    return () => {
      cancelled = true
    }
  }, [currentOrg?.id])

  return {
    isSponsor: hasSponsorSignal && hasPermission,
    loading: permLoading || signalLoading,
  }
}

/** Full portfolio fetch for the /sponsor dashboard. */
export function useSponsorPortfolio(): SponsorPortfolio {
  const { currentOrg } = useOrgStore()
  const [loading, setLoading] = useState(true)
  const [isSponsor, setIsSponsor] = useState(false)
  const [sponsorships, setSponsorships] = useState<SponsorshipWithChild[]>([])
  const [donations, setDonations] = useState<SponsorDonationRow[]>([])
  const [rollup, setRollup] = useState<PortfolioRollup>(EMPTY_ROLLUP)
  const [campaignTitlesById, setCampaignTitlesById] = useState<Map<string, string>>(new Map())
  const [memberCountsByOrg, setMemberCountsByOrg] = useState<Map<string, number>>(new Map())
  const [contactCountsByOrg, setContactCountsByOrg] = useState<Map<string, number>>(new Map())
  const [error, setError] = useState<string | null>(null)

  const fetchPortfolio = useCallback(async () => {
    if (!currentOrg?.id) {
      setLoading(false)
      setIsSponsor(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { data: rows, error: sErr } = await supabase
        .from("sponsorships")
        .select(SPONSORSHIP_SELECT)
        .eq("sponsor_org_id", currentOrg.id)
      if (sErr) throw sErr

      const links = (rows ?? []) as unknown as SponsorshipWithChild[]
      setSponsorships(links)
      setIsSponsor(links.length > 0)
      if (links.length === 0) {
        setDonations([])
        setRollup(EMPTY_ROLLUP)
        setLoading(false)
        return
      }

      const childIds = links.map((l) => l.child_org_id)

      const [donationsRes, campaignsRes, memberCounts, contactCounts] = await Promise.all([
        supabase
          .from("donations")
          .select("org_id, amount, created_at, status, donor_name, donor_email, campaign_id")
          .in("org_id", childIds)
          .eq("status", "succeeded")
          .order("created_at", { ascending: false })
          .limit(DONATIONS_FETCH_CAP),
        supabase.from("campaigns").select("id, title").in("org_id", childIds),
        Promise.all(
          childIds.map(async (orgId) => {
            const { count } = await supabase
              .from("organization_members")
              .select("id", { count: "exact", head: true })
              .eq("org_id", orgId)
            return [orgId, count ?? 0] as const
          })
        ),
        Promise.all(
          childIds.map(async (orgId) => {
            const { count } = await supabase
              .from("contacts")
              .select("id", { count: "exact", head: true })
              .eq("org_id", orgId)
            return [orgId, count ?? 0] as const
          })
        ),
      ])

      if (donationsRes.error) throw donationsRes.error
      const donationRows = (donationsRes.data ?? []) as SponsorDonationRow[]
      setDonations(donationRows)
      setRollup(aggregatePortfolio(donationRows))
      setCampaignTitlesById(
        new Map((campaignsRes.data ?? []).map((c) => [c.id as string, c.title as string]))
      )
      setMemberCountsByOrg(new Map(memberCounts))
      setContactCountsByOrg(new Map(contactCounts))
    } catch (err) {
      console.error("Sponsor portfolio fetch error:", err)
      setError(errorMessage(err, "Failed to load sponsor portfolio"))
    } finally {
      setLoading(false)
    }
  }, [currentOrg?.id])

  useEffect(() => {
    fetchPortfolio()
  }, [fetchPortfolio])

  return {
    loading,
    isSponsor,
    sponsorships,
    donations,
    rollup,
    campaignTitlesById,
    memberCountsByOrg,
    contactCountsByOrg,
    error,
    refresh: fetchPortfolio,
  }
}

export interface ChildOrgDetail {
  loading: boolean
  org: { id: string; name: string; slug: string; logo_url: string | null } | null
  sponsorship: SponsorshipWithChild | null
  donations: SponsorDonationRow[]
  donationsTotal: number
  contactCount: number
  error: string | null
  page: number
  pageSize: number
  setPage: (page: number) => void
  refresh: () => void
}

const DETAIL_PAGE_SIZE = 25

/** Per-child drill-down: org header, paginated donations, contact count. */
export function useChildOrgDetail(childOrgId: string | undefined): ChildOrgDetail {
  const { currentOrg } = useOrgStore()
  const [loading, setLoading] = useState(true)
  const [org, setOrg] = useState<ChildOrgDetail["org"]>(null)
  const [sponsorship, setSponsorship] = useState<SponsorshipWithChild | null>(null)
  const [donations, setDonations] = useState<SponsorDonationRow[]>([])
  const [donationsTotal, setDonationsTotal] = useState(0)
  const [contactCount, setContactCount] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [refreshIndex, setRefreshIndex] = useState(0)

  useEffect(() => {
    let cancelled = false
    async function fetchDetail() {
      if (!childOrgId || !currentOrg?.id) {
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const from = page * DETAIL_PAGE_SIZE
        const [orgRes, linkRes, donationsRes, contactsRes] = await Promise.all([
          supabase
            .from("organizations")
            .select("id, name, slug, logo_url")
            .eq("id", childOrgId)
            .maybeSingle(),
          supabase
            .from("sponsorships")
            .select(SPONSORSHIP_SELECT)
            .eq("child_org_id", childOrgId)
            .eq("sponsor_org_id", currentOrg.id)
            .maybeSingle(),
          supabase
            .from("donations")
            .select("org_id, amount, created_at, status, donor_name, donor_email, campaign_id", {
              count: "exact",
            })
            .eq("org_id", childOrgId)
            .eq("status", "succeeded")
            .order("created_at", { ascending: false })
            .range(from, from + DETAIL_PAGE_SIZE - 1),
          supabase
            .from("contacts")
            .select("id", { count: "exact", head: true })
            .eq("org_id", childOrgId),
        ])
        if (cancelled) return
        if (orgRes.error) throw orgRes.error
        if (donationsRes.error) throw donationsRes.error
        setOrg(orgRes.data ?? null)
        setSponsorship((linkRes.data as unknown as SponsorshipWithChild) ?? null)
        setDonations((donationsRes.data ?? []) as SponsorDonationRow[])
        setDonationsTotal(donationsRes.count ?? 0)
        setContactCount(contactsRes.count ?? 0)
      } catch (err) {
        if (!cancelled) {
          console.error("Child org detail fetch error:", err)
          setError(errorMessage(err, "Failed to load project"))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchDetail()
    return () => {
      cancelled = true
    }
  }, [childOrgId, currentOrg?.id, page, refreshIndex])

  return {
    loading,
    org,
    sponsorship,
    donations,
    donationsTotal,
    contactCount,
    error,
    page,
    pageSize: DETAIL_PAGE_SIZE,
    setPage,
    refresh: () => setRefreshIndex((i) => i + 1),
  }
}
